/**
 * Extract plain text only from resume files. No layout, no sections, no boxes.
 * Output is a single string (with newlines) for preprocessing and text-based parsing.
 */

/**
 * Extract text from PDF: reading order, one line per visual line, joined by \n.
 */
export async function extractTextFromPdf(file: File): Promise<string | null> {
  if (file.type !== 'application/pdf') return null;
  try {
    const [pdfjsLib, pdfWorker] = await Promise.all([
      import('pdfjs-dist/build/pdf.mjs'),
      import('pdfjs-dist/build/pdf.worker.mjs?url'),
    ]);
    (pdfjsLib as any).GlobalWorkerOptions.workerSrc = (pdfWorker as any).default;
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await (pdfjsLib as any).getDocument({ data: arrayBuffer }).promise;
    const Y_THRESHOLD = 5;
    const lines: string[] = [];

    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const viewport = page.getViewport({ scale: 1 });
      const textContent = await page.getTextContent({ includeMarkedContent: false });
      const items = (textContent.items || []) as Array<{ str: string; transform: number[] }>;

      const withPos = items
        .filter((item) => item.str)
        .map((item) => {
          const transform = item.transform || [];
          const x = transform[4] ?? 0;
          const y = viewport.height - (transform[5] ?? 0);
          return { str: item.str, x, y };
        });
      withPos.sort((a, b) => {
        if (Math.abs(a.y - b.y) > Y_THRESHOLD) return b.y - a.y;
        return a.x - b.x;
      });

      let lastY: number | null = null;
      let lineText = '';
      for (const { str, y } of withPos) {
        if (lastY !== null && Math.abs(y - lastY) > Y_THRESHOLD) {
          if (lineText.trim()) lines.push(lineText.trim());
          lineText = '';
        }
        lineText = lineText ? lineText + ' ' + str : str;
        lastY = y;
      }
      if (lineText.trim()) lines.push(lineText.trim());
    }

    return lines.length ? lines.join('\n') : null;
  } catch {
    return null;
  }
}

/**
 * Extract text from DOCX: body text with newlines between blocks. No headings/structure used.
 */
export async function extractTextFromDocx(file: File): Promise<string | null> {
  if (
    file.type !== 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' &&
    file.type !== 'application/msword'
  ) {
    return null;
  }
  try {
    const mammoth = await import('mammoth');
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.convertToHtml({ arrayBuffer });
    const html = result.value;
    if (!html || html.length < 10) return null;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const body = doc.body || doc.documentElement;
    if (!body) return null;

    const blocks: string[] = [];
    function getBlockText(el: Element): string {
      return (el.textContent || '').replace(/\s+/g, ' ').trim();
    }
    function collectBlocks(node: Node) {
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      const el = node as Element;
      const tag = el.tagName.toLowerCase();
      if (tag === 'p' || tag === 'div' || tag === 'li' || /^h[1-6]$/.test(tag)) {
        const t = getBlockText(el);
        if (t) blocks.push(t);
        return;
      }
      for (let i = 0; i < node.childNodes.length; i++) collectBlocks(node.childNodes[i]);
    }
    collectBlocks(body);
    const text = blocks.join('\n').replace(/\n{3,}/g, '\n\n').trim();
    return text.length ? text : null;
  } catch {
    return null;
  }
}

/**
 * Extract plain text from a resume file (PDF or DOCX), then normalize for parsing/preview.
 * Removes junk, fixes bullets/symbols from open-source tools, so LLM and section parser get clean text.
 */
export async function extractResumeText(file: File): Promise<string | null> {
  let raw: string | null = null;
  if (file.type === 'application/pdf') raw = await extractTextFromPdf(file);
  else if (
    file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    file.type === 'application/msword'
  ) {
    raw = await extractTextFromDocx(file);
  }
  if (!raw || !raw.trim()) return null;
  const { preprocessResumeText } = await import('./pdfTextSanitizer');
  return preprocessResumeText(raw);
}
